import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { SIGN_IN_SUCCESS, STORE_SIGN_IN_AUTH_DATA } from "../screens/login/actions"
import {
    LOAD_ACCOUNTS_SUCCESS,
    LOAD_AUTH_LOCATIONS_SUCCESS,
    LOAD_ORGANIZATIONS_SUCCESS,
    LOG_OUT_FAILURE,
    LOG_OUT_SUCCESS,
} from "../layouts/header/actions"
import {
    ACCOUNT_DOMAIN_UPDATED,
    ACCOUNT_SELECTED,
    AUTH_LOCATIONS_REFRESHED,
    COOKIE_LOADED,
    ORGANIZATION_SELECTED,
} from "./actions"
import type { LocationAuthData } from "./types"
import { OrganizationData } from "./types"
import {
    LOCATION_CREATED,
    LOCATION_DELETED,
    LOCATION_UPDATED,
    TERMINAL_CREATED,
    TERMINAL_UPDATED,
} from "../data/actions"
import type { AuthAccount2, AuthLocation } from "../../graphql/types"
import { DELETE_TERMINAL_CLICKED } from "../screens/locations/actions"
import { RELOAD_AUTH_ACCOUNTS_DATA } from "../screens/organization/actions"

export type AuthState = {
    session: Maybe<string>
    accounts: Maybe<AuthAccount2[]>
    organizations: Maybe<OrganizationData[]>
    locations: Maybe<LocationAuthData[]>
    selectedAccountId: Maybe<string>
    selectedOrganizationId: Maybe<string>
    role: Maybe<string>
    userId: Maybe<string>
    userEmail: Maybe<string>
    masterListAccess: boolean
}

export const createDefaultAuthState = () => ({
    session: null,
    accounts: null,
    organizations: null,
    locations: null,
    selectedAccountId: null,
    selectedOrganizationId: null,
    role: null,
    userId: null,
    userEmail: null,
    masterListAccess: false,
})
export const authReducer = (state: Maybe<AuthState> = undefined, action: Record<string, any>) => {
    if (!state) {
        return createDefaultAuthState()
    }

    switch (action.type) {
        case COOKIE_LOADED: {
            return {
                ...state,
                session: action.payload.session,
                selectedAccountId: action.payload.accountId,
                selectedOrganizationId: action.payload.organizationId,
                role: action.payload.userRole,
                userId: action.payload.userId,
                userEmail: action.payload.userEmail,
            }
        }

        case STORE_SIGN_IN_AUTH_DATA:
        case SIGN_IN_SUCCESS:
            return {
                ...state,
                session: action.payload.session,
                role: action.payload.role,
                userId: action.payload.userId,
                userEmail: action.payload.userEmail,
            }

        case LOAD_ACCOUNTS_SUCCESS:
            return { ...state, accounts: action.payload.accounts }

        case LOAD_ORGANIZATIONS_SUCCESS:
            return { ...state, organizations: action.payload.organizations }

        case LOAD_AUTH_LOCATIONS_SUCCESS:
        case AUTH_LOCATIONS_REFRESHED:
            return { ...state, locations: action.payload.locations, masterListAccess: action.payload.masterListAccess }

        case RELOAD_AUTH_ACCOUNTS_DATA:
            return { ...state, accounts: null, organizations: null, locations: null }

        case ACCOUNT_SELECTED:
            return {
                ...state,
                selectedAccountId: action.payload.accountId,
                locations: null,
            }

        case ORGANIZATION_SELECTED:
            return {
                ...state,
                selectedOrganizationId: action.payload.id,
            }

        case LOCATION_CREATED: {
            const authLocation: AuthLocation = {
                id: action.payload.location.id,
                name: action.payload.location.name,
                timezone: action.payload.location.timezone,
                terminals: [],
            }
            return { ...state, locations: [...(state.locations || []), authLocation] }
        }

        case LOCATION_UPDATED: {
            return {
                ...state,
                locations: (state.locations || []).map((l: LocationAuthData) =>
                    l.id !== action.payload.newValue.id ? l : { ...l, name: action.payload.newValue.name }
                ),
            }
        }

        case LOCATION_DELETED: {
            return {
                ...state,
                locations: (state.locations || []).filter(l => l.id !== action.payload.locationId),
            }
        }

        case TERMINAL_UPDATED: {
            return {
                ...state,
                locations: (state.locations || []).map(location =>
                    location.id !== action.payload.newValue.locationId
                        ? location
                        : {
                              ...location,
                              terminals: location.terminals.map(t =>
                                  t.id !== action.payload.newValue.id
                                      ? t
                                      : {
                                            ...t,
                                            ...(action.payload.newValue.name
                                                ? { name: action.payload.newValue.name }
                                                : {}),
                                            ...(action.payload.newValue.metadata
                                                ? { metadata: action.payload.newValue.metadata }
                                                : {}),
                                        }
                              ),
                          }
                ),
            }
        }

        case TERMINAL_CREATED: {
            return {
                ...state,
                locations: (state.locations || []).map(location =>
                    location.id !== action.payload.terminal.locationId
                        ? location
                        : { ...location, terminals: [...location.terminals, action.payload.terminal] }
                ),
            }
        }

        case DELETE_TERMINAL_CLICKED: {
            return {
                ...state,
                locations: (state.locations || []).map(location =>
                    location.id !== action.payload.locationId
                        ? location
                        : { ...location, terminals: location.terminals.filter(t => t.id !== action.payload.terminalId) }
                ),
            }
        }

        case ACCOUNT_DOMAIN_UPDATED: {
            return {
                ...state,
                accounts: (state.accounts || []).map(account =>
                    account.id !== action.payload.accountId
                        ? account
                        : { ...account, domainName: action.payload.domain }
                ),
            }
        }

        case LOG_OUT_SUCCESS:
        case LOG_OUT_FAILURE:
            return createDefaultAuthState()

        default:
            return state
    }
}
